import { useMemo, useState } from "react";

import type {
  AllocationInputRow,
  AllocationType,
  InvoiceItem,
  NonCoveredReason,
} from "@/shared/types/billing";
import { formatVndWithUnit } from "@/shared/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Ai trả phần nào của một dòng hoá đơn (BHYT Phase 1, #18837).
 *
 * <p><b>Speaks the cashier's language, not the schema's.</b> The API stores a payer plus an
 * allocation type plus a reason; a cashier thinks in four lines on a bill. So the four rows below
 * are fixed and named for what they mean — BHYT chi trả, đồng chi trả, ngoài mức BHYT, không được
 * BHYT chi trả — and the component maps each to its (payer, type, reason) triple on save. Nobody
 * has to know what NON_COVERED means to use this.
 *
 * <p><b>The invariant is shown, not enforced by rejection.</b> The server refuses a set that does
 * not sum to the line total (422). Discovering that after pressing save is a bad way to learn it,
 * so the running total and the shortfall are on screen while typing and the save button stays
 * disabled until they agree.
 *
 * <p><b>It calculates nothing.</b> No benefit rate is derived from the card, no 80/20 is
 * suggested: the BHYT rules are still open (#18828). The person decides the numbers; the screen
 * only checks they add up.
 */

/** The rows a cashier sees, in the order money flows off the bill. */
const ROWS: {
  key: string;
  label: string;
  hint: string;
  payer: "BHYT" | "PATIENT";
  type: AllocationType;
  reason: NonCoveredReason | null;
}[] = [
  {
    key: "covered",
    label: "BHYT chi trả",
    hint: "Phần quỹ BHYT thanh toán",
    payer: "BHYT",
    type: "COVERED",
    reason: null,
  },
  {
    key: "copay",
    label: "Bệnh nhân đồng chi trả",
    hint: "Phần bệnh nhân trả trong mức BHYT",
    payer: "PATIENT",
    type: "CO_PAY",
    reason: null,
  },
  {
    key: "above",
    label: "Chênh lệch ngoài mức BHYT",
    hint: "Phần giá dịch vụ vượt mức BHYT làm căn cứ",
    payer: "PATIENT",
    type: "NON_COVERED",
    reason: "ABOVE_TARIFF",
  },
  {
    key: "notcovered",
    label: "Không được BHYT chi trả",
    hint: "Dịch vụ ngoài danh mục BHYT",
    payer: "PATIENT",
    type: "NON_COVERED",
    reason: "SERVICE_NOT_COVERED",
  },
];

/** Reads an existing allocation set back into the four rows. */
function toAmounts(item: InvoiceItem): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of ROWS) out[r.key] = 0;
  for (const a of item.allocations ?? []) {
    const row = ROWS.find(
      (r) =>
        r.payer === a.payer &&
        r.type === a.allocationType &&
        (r.reason ?? null) === (a.nonCoveredReason ?? null)
    );
    // An allocation the four rows cannot express (e.g. NO_BHYT, written by an import) is folded
    // into "không được BHYT chi trả" rather than dropped — losing money on screen is worse than
    // showing it under a slightly broad label.
    if (row) out[row.key] += a.amountVnd;
    else out.notcovered += a.amountVnd;
  }
  return out;
}

export function LineAllocationEditor({
  item,
  disabled,
  saving,
  onSave,
  onCancel,
}: {
  item: InvoiceItem;
  /** True when the invoice can no longer be edited — the server would refuse anyway. */
  disabled: boolean;
  saving: boolean;
  onSave: (rows: AllocationInputRow[]) => void;
  onCancel: () => void;
}) {
  const [amounts, setAmounts] = useState<Record<string, number>>(() => toAmounts(item));
  const [basis, setBasis] = useState<string>(() => {
    const withBasis = (item.allocations ?? []).find((a) => a.basisAmountVnd != null);
    return withBasis?.basisAmountVnd != null ? String(withBasis.basisAmountVnd) : "";
  });

  const allocated = useMemo(
    () => ROWS.reduce((s, r) => s + (amounts[r.key] || 0), 0),
    [amounts]
  );
  const remaining = item.lineTotal - allocated;
  const balanced = remaining === 0;
  const patientTotal = ROWS.filter((r) => r.payer === "PATIENT").reduce(
    (s, r) => s + (amounts[r.key] || 0),
    0
  );
  const bhytTotal = amounts.covered || 0;

  function set(key: string, raw: string) {
    const n = Number(raw.replace(/[^\d]/g, ""));
    setAmounts((p) => ({ ...p, [key]: Number.isFinite(n) ? n : 0 }));
  }

  function save() {
    const basisNum = basis.trim() === "" ? null : Number(basis.replace(/[^\d]/g, ""));
    const rows: AllocationInputRow[] = ROWS.filter((r) => (amounts[r.key] || 0) > 0).map((r) => ({
      payer: r.payer,
      allocationType: r.type,
      amountVnd: amounts[r.key],
      nonCoveredReason: r.reason,
      // The tariff is a property of the line, so it goes on every row that was decided against
      // it. Rows that have nothing to do with the tariff carry null.
      basisAmountVnd: r.type === "NON_COVERED" && r.reason === "SERVICE_NOT_COVERED" ? null : basisNum,
      benefitPercent: null,
      note: null,
    }));
    onSave(rows);
  }

  return (
    <div className="rounded-md border bg-muted/30 p-4" data-testid="allocation-editor">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h4 className="text-sm font-semibold">Phân bổ nguồn chi trả — {item.name}</h4>
        <span className="font-mono text-sm tabular-nums">
          Thành tiền: <strong>{formatVndWithUnit(item.lineTotal)}</strong>
        </span>
      </div>

      <div className="mb-3 max-w-xs space-y-1">
        <label className="text-xs text-muted-foreground" htmlFor="alloc-basis">
          Mức BHYT làm căn cứ (nếu có)
        </label>
        <Input
          id="alloc-basis"
          inputMode="numeric"
          value={basis}
          disabled={disabled}
          onChange={(e) => setBasis(e.target.value)}
          placeholder="VD: 300000"
        />
      </div>

      <div className="space-y-2">
        {ROWS.map((r) => (
          <div key={r.key} className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm">{r.label}</div>
              <div className="text-xs text-muted-foreground">{r.hint}</div>
            </div>
            <Input
              aria-label={r.label}
              className="w-40 text-right font-mono tabular-nums"
              inputMode="numeric"
              disabled={disabled}
              value={amounts[r.key] === 0 ? "" : String(amounts[r.key])}
              onChange={(e) => set(r.key, e.target.value)}
              placeholder="0"
            />
          </div>
        ))}
      </div>

      <div className="mt-3 border-t pt-3 text-sm">
        <div className="flex items-center justify-between">
          <span>Đã phân bổ</span>
          <span className="font-mono tabular-nums">{formatVndWithUnit(allocated)}</span>
        </div>
        {!balanced && (
          <div
            className="mt-1 flex items-center justify-between font-medium text-destructive"
            role="alert"
          >
            <span>{remaining > 0 ? "Còn thiếu" : "Vượt quá"}</span>
            <span className="font-mono tabular-nums">
              {formatVndWithUnit(Math.abs(remaining))}
            </span>
          </div>
        )}
        {balanced && (
          <div className="mt-1 space-y-1 text-muted-foreground" data-testid="allocation-summary">
            <div className="flex items-center justify-between">
              <span>BHYT chịu</span>
              <span className="font-mono tabular-nums">{formatVndWithUnit(bhytTotal)}</span>
            </div>
            <div className="flex items-center justify-between font-medium text-foreground">
              <span>Bệnh nhân chịu</span>
              <span className="font-mono tabular-nums">{formatVndWithUnit(patientTotal)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Huỷ
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={save}
          // The server rejects an unbalanced set; there is no reason to let someone press this
          // and find out the hard way.
          disabled={disabled || saving || !balanced}
          title={balanced ? undefined : "Tổng phân bổ phải bằng thành tiền của dòng"}
        >
          {saving ? "Đang lưu…" : "Lưu phân bổ"}
        </Button>
      </div>
    </div>
  );
}
